import isDate from 'lodash/isDate.js';
import isNumber from 'lodash/isNumber.js';
import isString from 'lodash/isString.js';
import clamp from 'lodash/clamp.js';
import isPlainObject from 'lodash/isPlainObject.js';
import { parse, format } from './fecha.js';

const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
class Locale {
  constructor(_ref) {
    let {
      id,
      firstDayOfWeek,
      masks
    } = _ref;
    this.id = id;
    this.firstDayOfWeek = clamp(firstDayOfWeek, 1, 7);
    this.masks = masks;
    this.dayNames = this.getDayNames('long');
    this.dayNamesShort = this.getDayNames('short');
    this.dayNamesShorter = this.dayNamesShort.map(s => s.substring(0, 2));
    this.dayNamesNarrow = this.getDayNames('narrow');
    this.monthNames = this.getMonthNames('long');
    this.monthNamesShort = this.getMonthNames('short');
    this.monthData = {};
    this.getMonthComps = this.getMonthComps.bind(this);
    this.parse = this.parse.bind(this);
    this.format = this.format.bind(this);
    this.toDate = this.toDate.bind(this);
  }
  parse(dateStr, mask) {
    return parse(dateStr, mask || this.masks.L, this);
  }
  format(date, mask) {
    return format(date, mask || this.masks.L, this);
  }
  toDate(dt, mask) {
    if (isDate(dt)) {
      return new Date(dt.getTime());
    }
    if (isNumber(dt)) {
      return new Date(dt);
    }
    if (isString(dt)) {
      return this.parse(dt, mask);
    }
    if (isPlainObject(dt)) {
      const date = new Date();
      return new Date(dt.year || date.getFullYear(), dt.month || date.getMonth(), dt.day || date.getDate());
    }
    return dt;
  }
  getMonthDates() {
    let year = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 2000;
    const dates = [];
    for (let i = 0; i < 12; i++) {
      dates.push(new Date(year, i, 15));
    }
    return dates;
  }
  getMonthNames(length) {
    const dtf = new Intl.DateTimeFormat(this.id, {
      month: length,
      timeZone: 'UTC'
    });
    return this.getMonthDates().map(date => dtf.format(date));
  }
  getWeekdayDates() {
    let {
      year = 2000,
      utc = false,
      firstDayOfWeek = this.firstDayOfWeek
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const dates = [];
    for (let i = 1, j = 0; j < 7; i++) {
      const date = utc ? new Date(Date.UTC(year, 0, i)) : new Date(year, 0, i);
      const day = utc ? date.getUTCDay() : date.getDay();
      if (day === firstDayOfWeek - 1 || j > 0) {
        dates.push(date);
        j++;
      }
    }
    return dates;
  }
  getDayNames(length) {
    const dtf = new Intl.DateTimeFormat(this.id, {
      weekday: length,
      timeZone: 'UTC'
    });
    return this.getWeekdayDates({
      firstDayOfWeek: 1,
      utc: true
    }).map(date => dtf.format(date));
  }
  getMonthComps(month, year) {
    const key = `${month}-${year}`;
    let comps = this.monthData[key];
    if (!comps) {
      const inLeapYear = year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
      const firstWeekday = new Date(year, month - 1, 1).getDay() + 1;
      const days = month === 2 && inLeapYear ? 29 : daysInMonths[month - 1];
      const weeks = Math.ceil((days + Math.abs(firstWeekday - this.firstDayOfWeek)) / 7);
      comps = {
        firstDayOfWeek: this.firstDayOfWeek,
        inLeapYear,
        firstWeekday,
        days,
        weeks,
        month,
        year
      };
      this.monthData[key] = comps;
    }
    return comps;
  }
  getThisMonthComps() {
    const date = new Date();
    return this.getMonthComps(date.getMonth() + 1, date.getFullYear());
  }
  getPrevMonthComps(month, year) {
    if (month === 1) {
      return this.getMonthComps(12, year - 1);
    }
    return this.getMonthComps(month - 1, year);
  }
  getNextMonthComps(month, year) {
    if (month === 12) {
      return this.getMonthComps(1, year + 1);
    }
    return this.getMonthComps(month + 1, year);
  }
  getDayFromDate(date) {
    if (!date) {
      return null;
    }
    const month = date.getMonth() + 1;
    const year = date.getUTCFullYear();
    const comps = this.getMonthComps(month, year);
    const day = date.getDate();
    const dayFromEnd = comps.days - day + 1;
    const weekday = date.getDay() + 1;
    const weekdayOrdinal = Math.floor((day - 1) / 7 + 1);
    const weekdayOrdinalFromEnd = Math.floor((comps.days - day) / 7 + 1);
    const week = Math.ceil((day + Math.abs(comps.firstWeekday - comps.firstDayOfWeek)) / 7);
    const weekFromEnd = comps.weeks - week + 1;
    return {
      day,
      dayFromEnd,
      weekday,
      weekdayOrdinal,
      weekdayOrdinalFromEnd,
      week,
      weekFromEnd,
      month,
      year,
      date,
      dateTime: date.getTime()
    };
  }
  getCalendarDays(page) {
    const days = [];
    const {
      monthComps,
      prevMonthComps,
      nextMonthComps
    } = page;
    const {
      firstDayOfWeek,
      firstWeekday
    } = monthComps;
    const prevMonthDaysToShow = firstWeekday + (firstWeekday < firstDayOfWeek ? 7 : 0) - firstDayOfWeek;
    let prevMonth = true;
    let thisMonth = false;
    let nextMonth = false;
    let day = prevMonthComps.days - prevMonthDaysToShow + 1;
    let dayFromEnd = prevMonthComps.days - day + 1;
    let weekdayOrdinal = Math.floor((day - 1) / 7 + 1);
    let weekdayOrdinalFromEnd = 1;
    let week = prevMonthComps.weeks;
    let weekFromEnd = 1;
    let {
      month,
      year
    } = prevMonthComps;
    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth() + 1;
    const todayYear = today.getFullYear();
    for (let wk = 1; wk <= 6; wk++) {
      for (let i = 1, weekday = firstDayOfWeek; i <= 7; i++, weekday += weekday === 7 ? -6 : 1) {
        if (prevMonth && weekday === firstWeekday) {
          day = 1;
          dayFromEnd = monthComps.days;
          weekdayOrdinal = Math.floor((day - 1) / 7 + 1);
          weekdayOrdinalFromEnd = Math.floor((monthComps.days - day) / 7 + 1);
          week = 1;
          weekFromEnd = monthComps.weeks;
          month = monthComps.month;
          year = monthComps.year;
          prevMonth = false;
          thisMonth = true;
        }
        const date = new Date(year, month - 1, day);
        const weekdayPosition = i;
        const weekdayPositionFromEnd = 7 - i;
        const isToday = day === todayDay && month === todayMonth && year === todayYear;
        const isFirstDay = thisMonth && day === 1;
        const isLastDay = thisMonth && day === monthComps.days;
        const onTop = wk === 1;
        const onBottom = wk === 6;
        const onLeft = i === 1;
        const onRight = i === 7;
        days.push({
          id: `${month}-${day}`,
          label: day.toString(),
          day,
          dayFromEnd,
          weekday,
          weekdayPosition,
          weekdayPositionFromEnd,
          weekdayOrdinal,
          weekdayOrdinalFromEnd,
          week,
          weekFromEnd,
          month,
          year,
          date,
          dateTime: date.getTime(),
          isToday,
          isFirstDay,
          isLastDay,
          inMonth: thisMonth,
          inPrevMonth: prevMonth,
          inNextMonth: nextMonth,
          onTop,
          onBottom,
          onLeft,
          onRight,
          classes: [`day-${day}`, `day-from-end-${dayFromEnd}`, `weekday-${weekday}`, `weekday-position-${weekdayPosition}`, `weekday-ordinal-${weekdayOrdinal}`, `weekday-ordinal-from-end-${weekdayOrdinalFromEnd}`, `week-${week}`, `week-from-end-${weekFromEnd}`, {
            'is-today': isToday,
            'is-first-day': isFirstDay,
            'is-last-day': isLastDay,
            'in-month': thisMonth,
            'in-prev-month': prevMonth,
            'in-next-month': nextMonth,
            'on-top': onTop,
            'on-bottom': onBottom,
            'on-left': onLeft,
            'on-right': onRight
          }],
          attribute: null
        });
        if (thisMonth && isLastDay) {
          thisMonth = false;
          nextMonth = true;
          day = 1;
          dayFromEnd = nextMonthComps.days;
          weekdayOrdinal = 1;
          weekdayOrdinalFromEnd = Math.floor((nextMonthComps.days - day) / 7 + 1);
          week = 1;
          weekFromEnd = nextMonthComps.weeks;
          month = nextMonthComps.month;
          year = nextMonthComps.year;
        } else {
          day++;
          dayFromEnd--;
          weekdayOrdinal = Math.floor((day - 1) / 7 + 1);
          weekdayOrdinalFromEnd = Math.floor((monthComps.days - day) / 7 + 1);
        }
      }
      week++;
      weekFromEnd--;
    }
    if (days[days.length - 7].inNextMonth) {
      days.length -= 7;
    }
    return days;
  }
}

export { Locale as default };
//# sourceMappingURL=locale.js.map
